import React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import http from 'helpers/requests';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CgFilterSelect } from 'modules/Common/ui/CgFilterDropdown';
import CgBlockLoader from 'modules/Common/ui/CgBlockLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import Decimal from 'decimal.js';
import { getCbamFactorForYear, calculateSefa, useCnGoods } from 'modules/Common/utils/Utility';
import { useBranding } from 'modules/App/hooks/useBranding';
import { cn } from 'modules/Common/utils/cn';
import {
  FormSection,
  FormField,
  TextField,
  SelectField,
  ErrorText,
  inputCls,
  sectionTitleClass,
} from 'modules/Common/form/v2';
import CnGoodField from './CnGoodField';
import { buildUkEmissionsLayout } from 'modules/Installations/config/emissionsFormLayout';
import CgInfoHint from 'modules/Common/ui/CgInfoHint';
import CgDatePicker from 'modules/Common/form/CgDatePicker';
import { recordFieldAnchor } from 'modules/Common/utils/recordLanding';
import { controlAnchorProps, gapGroupProps, gapPartProps } from 'modules/Common/utils/gapAnchor';
import useEmissionStore from 'modules/Suppliers/stores/emissionStore';
import { useTranslation } from 'react-i18next';
import { handleTablistKeys } from 'modules/Common/hooks/useTablistKeys';

dayjs.extend(utc);

const getPeriodDates = (period, year) => {
  const periods = {
    Q1: { start: `${year}-01-01`, end: `${year}-03-31` },
    Q2: { start: `${year}-04-01`, end: `${year}-06-30` },
    Q3: { start: `${year}-07-01`, end: `${year}-09-30` },
    Q4: { start: `${year}-10-01`, end: `${year}-12-31` },
    Annual: { start: `${year}-01-01`, end: `${year}-12-31` },
  };
  return periods[period];
};

const getAvailableYears = () => {
  const currentYear = dayjs().year();
  return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
};

const buildElectricitySourceOptions = (t) => [
  {
    value: 'SOE01',
    label: t(
      'installations:electricitySource.soe01',
      'Direct technical link to electricity generator (SOE01)',
    ),
  },
  {
    value: 'SOE02',
    label: t(
      'installations:electricitySource.soe02',
      '(Bilateral) power purchase agreement (SOE02)',
    ),
  },
  {
    value: 'SOE03',
    label: t('installations:electricitySource.soe03', 'Received from the grid (SOE03)'),
  },
];

export function EmissionsDataTab({
  setCategoryProductionMethods,
  lockForm,
  contextType,
  installationId,
  operatorId,
  // UK importer form (backend docs/features/uk-cbam-emission-factors, S7; D12, D13).
  isUk = false,
}) {
  const { t } = useTranslation('installations');
  const electricitySourceOptions = useMemo(() => buildElectricitySourceOptions(t), [t]);
  const brand = useBranding();
  const cnGoods = useCnGoods();
  const {
    setValue,
    control,
    formState: { errors },
  } = useFormContext();

  const { selectedEmission } = useEmissionStore();

  const [selectedYear, setSelectedYear] = useState(() => dayjs().year());
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [isFetchingBenchmark, setIsFetchingBenchmark] = useState(false);
  const [isFetchingCustomBenchmark, setIsFetchingCustomBenchmark] = useState(false);
  const [benchmarkPrePopulated, setBenchmarkPrePopulated] = useState(false);
  const [prePopulatedYear, setPrePopulatedYear] = useState(null);
  const [noCustomBenchmarkFound, setNoCustomBenchmarkFound] = useState(false);

  const availableYears = useMemo(() => getAvailableYears(), []);

  const dateValue = useWatch({ control, name: 'date' });
  const lastRecordDateValue = useWatch({ control, name: 'lastRecordDate' });
  const indirectEmissionFactor = useWatch({ control, name: 'indirectEmissionFactor' });
  const electricityConsumed = useWatch({ control, name: 'electricityConsumed' });
  const useBenchmarkDefault = useWatch({ control, name: 'useBenchmarkDefault' });
  const benchmarkInputMode = useWatch({ control, name: 'benchmarkInputMode' });
  const cnGood = useWatch({ control, name: 'cnGood' });
  const benchmark = useWatch({ control, name: 'benchmark' });
  const sefa = useWatch({ control, name: 'sefa' });
  const supplierIdValue = useWatch({ control, name: 'supplierId' });
  const installationIdValue = useWatch({ control, name: 'installationId' });

  const reportingYear = useMemo(() => {
    if (dateValue) {
      const parsed = dayjs(dateValue);
      if (parsed.isValid()) return parsed.year();
    }
    return null;
  }, [dateValue]);

  const cbamFactor = useMemo(() => {
    if (reportingYear) return getCbamFactorForYear(reportingYear);
    return null;
  }, [reportingYear]);

  // Auto-calculate specificIndirectEmission — EU only. A UK importer enters indirect directly
  // (D12): there is no breakdown to derive it from, and nothing may overwrite it.
  useEffect(() => {
    if (isUk) return;
    if (indirectEmissionFactor && electricityConsumed) {
      try {
        const factor = new Decimal(indirectEmissionFactor);
        const consumption = new Decimal(electricityConsumed);
        if (factor.isPositive() && consumption.isPositive()) {
          setValue(
            'specificIndirectEmission',
            factor.times(consumption).toDecimalPlaces(7).toString(),
            { shouldValidate: true },
          );
          return;
        }
      } catch {}
    }
    setValue('specificIndirectEmission', '', { shouldValidate: false });
  }, [isUk, indirectEmissionFactor, electricityConsumed, setValue]);

  // Update cbamFactor and reportingYear in form when they change
  useEffect(() => {
    if (
      (contextType === 'operator' || contextType === 'declarant') &&
      cbamFactor &&
      reportingYear
    ) {
      setValue('cbamFactor', cbamFactor, { shouldValidate: false });
      setValue('reportingYear', reportingYear, { shouldValidate: false });
    }
  }, [contextType, cbamFactor, reportingYear, setValue]);

  const fetchBenchmark = useCallback(async () => {
    if (isUk || (contextType !== 'operator' && contextType !== 'declarant')) return;
    if (!useBenchmarkDefault || !cnGood || !reportingYear) return;

    setIsFetchingBenchmark(true);
    try {
      const params = new URLSearchParams();
      params.set('cnGood', cnGood);
      params.set('year', reportingYear.toString());
      if (operatorId) params.set('supplierId', operatorId);
      if (installationId) params.set('installationId', installationId);

      const resp = await http.get(`/cbam-benchmarks?${params.toString()}`);
      const benchmarkValue =
        typeof resp?.data === 'number'
          ? resp.data
          : (resp?.data?.benchmark ?? resp?.data?.value ?? null);

      if (benchmarkValue !== null) {
        const calculatedSefa = calculateSefa(benchmarkValue, cbamFactor);
        setValue('benchmark', String(benchmarkValue), { shouldValidate: false });
        setValue('sefa', String(calculatedSefa), { shouldValidate: false });
        setValue('cbamFactor', cbamFactor, { shouldValidate: false });
        setValue('reportingYear', reportingYear, { shouldValidate: false });
      }
    } catch (error) {
      console.error('Error fetching benchmark:', error);
    } finally {
      setIsFetchingBenchmark(false);
    }
  }, [
    isUk,
    contextType,
    useBenchmarkDefault,
    cnGood,
    reportingYear,
    cbamFactor,
    operatorId,
    installationId,
    setValue,
  ]);

  useEffect(() => {
    if (
      (contextType === 'operator' || contextType === 'declarant') &&
      useBenchmarkDefault &&
      cnGood &&
      reportingYear
    ) {
      fetchBenchmark();
    }
  }, [contextType, useBenchmarkDefault, cnGood, reportingYear, fetchBenchmark]);

  // Fetch existing CustomBenchmark for current cnGood+supplier+installation (declarant only)
  const fetchExistingCustomBenchmark = useCallback(async () => {
    if (!cnGood || !reportingYear) return;
    setIsFetchingCustomBenchmark(true);
    setNoCustomBenchmarkFound(false);
    try {
      const searchBody = { cnGood, page: 1, items: 20 };
      if (supplierIdValue) searchBody.supplierId = supplierIdValue;
      if (installationIdValue) searchBody.installationId = installationIdValue;

      const resp = await http.post('/custom-benchmarks/search', searchBody);
      const allBenchmarks = resp?.data?.benchmarks || [];
      const match =
        allBenchmarks
          .filter((b) => b.benchmark && (!b.year || b.year <= reportingYear))
          .sort((a, b) => (b.year || 0) - (a.year || 0))[0] || null;

      if (match?.benchmark) {
        const benchmarkValue = match.benchmark;
        const factor = cbamFactor || getCbamFactorForYear(reportingYear);
        const calculatedSefa = calculateSefa(benchmarkValue, factor);
        setValue('benchmark', String(benchmarkValue), { shouldValidate: false });
        setValue('sefa', String(calculatedSefa), { shouldValidate: false });
        setValue('cbamFactor', String(factor), { shouldValidate: false });
        setValue('reportingYear', String(reportingYear), { shouldValidate: false });
        setValue('useBenchmarkDefault', false);
        setBenchmarkPrePopulated(true);
        setPrePopulatedYear(match.year || null);
      } else {
        setNoCustomBenchmarkFound(true);
      }
    } catch (error) {
      console.error('Error fetching existing custom benchmark:', error);
      setNoCustomBenchmarkFound(true);
    } finally {
      setIsFetchingCustomBenchmark(false);
    }
  }, [cnGood, supplierIdValue, installationIdValue, reportingYear, cbamFactor, setValue]);

  // Auto pre-populate on mount for declarant editing emissions without SEFA
  useEffect(() => {
    const hasSefaOnEmission = selectedEmission?.sefa?.benchmark;
    if (
      isUk ||
      contextType !== 'declarant' ||
      !cnGood ||
      !reportingYear ||
      hasSefaOnEmission ||
      !selectedEmission
    ) {
      return;
    }
    fetchExistingCustomBenchmark();
  }, [isUk, contextType, cnGood, reportingYear, selectedEmission, fetchExistingCustomBenchmark]);

  // Detect period from form dates. Q3 (S7 fix batch 3): this used to run
  // ONCE on mount (`[]` deps) — reading dateValue/lastRecordDateValue via a
  // stale closure from the very first render, before a caller-seeded
  // `selectedEmission` (e.g. RequestDirectEntryPage's request-line seed)
  // resets the form on a LATER effect. The quick-select year/quarter buttons
  // never picked up the seeded period as a result. Watching the actual
  // values keeps this in sync with every source of a date change — a seed
  // landing after mount, the quick-select controls themselves, or the raw
  // text inputs.
  useEffect(() => {
    if (dateValue && lastRecordDateValue) {
      const startDate = dayjs(dateValue);
      const endDate = dayjs(lastRecordDateValue);
      if (startDate.isValid() && endDate.isValid()) {
        const year = startDate.year();
        setSelectedYear(year);
        for (const p of ['Q1', 'Q2', 'Q3', 'Q4', 'Annual']) {
          const pDates = getPeriodDates(p, year);
          if (dateValue === pDates.start && lastRecordDateValue === pDates.end) {
            setSelectedPeriod(p);
            return;
          }
        }
        setSelectedPeriod(null);
      }
    }
  }, [dateValue, lastRecordDateValue]);

  const handlePeriodSelect = (period) => {
    if (lockForm) return;
    setSelectedPeriod(period);
    const dates = getPeriodDates(period, selectedYear);
    setValue('date', dates.start, { shouldValidate: true });
    setValue('lastRecordDate', dates.end, { shouldValidate: true });
  };

  const handleYearChange = (year) => {
    if (lockForm) return;
    setSelectedYear(year);
    if (selectedPeriod) {
      const dates = getPeriodDates(selectedPeriod, year);
      setValue('date', dates.start, { shouldValidate: true });
      setValue('lastRecordDate', dates.end, { shouldValidate: true });
    }
  };

  // Custom period via the date pickers: any manual date invalidates the
  // quick-select highlight (the dates no longer match a canned period).
  const handleDateChange = (fieldName, value) => {
    setSelectedPeriod(null);
    setValue(fieldName, value, { shouldValidate: true });
  };

  const handleBenchmarkChange = (value) => {
    setValue('benchmark', value, { shouldValidate: false });
    const benchmarkVal = parseFloat(value);
    if (!isNaN(benchmarkVal) && cbamFactor) {
      setValue('sefa', String(calculateSefa(benchmarkVal, cbamFactor)), { shouldValidate: false });
    }
  };

  const handleSefaChange = (value) => {
    setValue('sefa', value, { shouldValidate: false });
  };

  const handleUseBenchmarkDefaultChange = (checked) => {
    setValue('useBenchmarkDefault', checked);
    if (!checked) {
      setValue('benchmark', '');
      setValue('sefa', '');
    }
  };

  const handleBenchmarkInputModeChange = (mode) => {
    setValue('benchmarkInputMode', mode);
  };

  return (
    <div className="emissions-data-tab">
      {/* Quick Period Selection — v2 system (matches the modal's section
          cards): year via CgFilterSelect, quarters as a segmented pill group. */}
      <div
        className="mb-4 p-3 rounded-xl border border-slate-200 bg-slate-50"
        data-tour="reporting-period-section"
      >
        <p className="text-xs font-medium uppercase tracking-wide text-slate-600 m-0 mb-2">
          {t('emissions.quickSelectHeading', 'Reporting period — quick select')}
        </p>
        <div className="flex items-center flex-wrap gap-2">
          <div className="w-28">
            <CgFilterSelect
              options={availableYears.map((year) => ({ value: year, label: String(year) }))}
              value={selectedYear}
              onChange={(val) => handleYearChange(Number(val))}
              disabled={lockForm}
              placeholder={t('fields.year', 'Year')}
            />
          </div>

          <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
            {['Q1', 'Q2', 'Q3', 'Q4'].map((q, i) => (
              <button
                key={q}
                type="button"
                onClick={() => handlePeriodSelect(q)}
                disabled={lockForm}
                aria-pressed={selectedPeriod === q}
                className={`px-3 py-1.5 text-xs font-semibold min-w-[45px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 disabled:opacity-50 disabled:cursor-not-allowed ${
                  i > 0 ? 'border-l border-slate-200' : ''
                } ${
                  selectedPeriod === q
                    ? 'bg-primaryColor text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {q}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => handlePeriodSelect('Annual')}
            disabled={lockForm}
            aria-pressed={selectedPeriod === 'Annual'}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 disabled:opacity-50 disabled:cursor-not-allowed ${
              selectedPeriod === 'Annual'
                ? 'bg-primaryColor text-white border-primaryColor'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <FontAwesomeIcon
              icon={faIcons.faCalendarAlt}
              className="text-[11px]"
            />
            {t('fields.annual', 'Annual')}
          </button>

          {selectedPeriod && (
            <span className="inline-flex items-center gap-1 text-xs text-green-700 ml-1">
              <FontAwesomeIcon
                icon={faIcons.faCheck}
                className="text-[10px]"
              />
              {dayjs(getPeriodDates(selectedPeriod, selectedYear).start).format('DD/MM/YYYY')} to{' '}
              {dayjs(getPeriodDates(selectedPeriod, selectedYear).end).format('DD/MM/YYYY')}
            </span>
          )}
        </div>

        <p className="flex items-center gap-1 text-[11px] text-slate-400 m-0 mt-2">
          <FontAwesomeIcon icon={faIcons.faInfoCircle} />
          {t(
            'fields.selectPeriodHint',
            'Select a reporting period above to auto-fill dates, or type custom dates below.',
          )}
        </p>
      </div>

      {/* Custom reporting period — themable Radix calendar (CgDatePicker),
          type/pick either. The quick-select above is a shortcut; setting any
          custom date here clears its highlight. Stored as YYYY-MM-DD. */}
      <div className="grid grid-cols-12 gap-3 mb-3">
        <div className="col-span-12 md:col-span-6">
          <label
            htmlFor="date"
            className="block text-xs font-medium uppercase tracking-wide text-slate-600 mb-1"
          >
            {t('emissions.periodStartLabel', 'Reporting period start *')}
          </label>
          <CgDatePicker
            id="date"
            anchor={recordFieldAnchor('date')}
            disabled={lockForm}
            error={!!errors.date}
            value={dateValue ? dayjs.utc(dateValue).format('YYYY-MM-DD') : ''}
            onChange={(val) => handleDateChange('date', val)}
          />
          <ErrorText>{errors.date?.message}</ErrorText>
        </div>
        <div className="col-span-12 md:col-span-6">
          <label
            htmlFor="lastRecordDate"
            className="block text-xs font-medium uppercase tracking-wide text-slate-600 mb-1"
          >
            {t('emissions.periodEndLabel', 'Reporting period end *')}
          </label>
          <CgDatePicker
            id="lastRecordDate"
            anchor={recordFieldAnchor('lastRecordDate')}
            disabled={lockForm}
            error={!!errors.lastRecordDate}
            value={lastRecordDateValue ? dayjs.utc(lastRecordDateValue).format('YYYY-MM-DD') : ''}
            onChange={(val) => handleDateChange('lastRecordDate', val)}
          />
          <ErrorText>{errors.lastRecordDate?.message}</ErrorText>
        </div>
      </div>

      {/* Product Details */}
      <FormSection
        title={t('production.productDetails', 'Product Details')}
        compact
      >
        <FormField
          name="cnGood"
          label={t('fields.cnGood', 'CN Good')}
          required
          colSpan={6}
          data-tour="cn-code-field"
        >
          <CnGoodField
            cnGoods={cnGoods}
            setCategoryProductionMethods={setCategoryProductionMethods}
            setValue={setValue}
            disabled={lockForm}
          />
        </FormField>

        <FormField
          name="operatorGoodName"
          label={t('production.productId', 'Product ID')}
          colSpan={6}
          popoverTitle={t('production.productIdPopoverTitle', 'What is a Product ID?')}
          popoverContent={
            <span className="text-sm">
              {t(
                'production.productIdPopoverBody',
                'The Product ID is a unique identifier or name you assign to distinguish your CBAM products. This could be an internal reference code, SKU, or descriptive name used on your invoices with your clients.',
              )}
            </span>
          }
        >
          <TextField disabled={lockForm} />
        </FormField>
      </FormSection>

      {/* Embedded emissions. A UK importer types in direct and an optional indirect figure
          (D12), arranged by config/emissionsFormLayout; the EU form starts with direct. */}
      {isUk ? (
        buildUkEmissionsLayout(t).map((section) => (
          <FormSection
            key={section.key}
            title={section.title}
            compact
          >
            {section.fields.map((entry) => (
              <FormField
                key={entry.name}
                name={entry.name}
                anchor={
                  entry.name === 'specificDirectEmission'
                    ? recordFieldAnchor('specificDirectEmission')
                    : null
                }
                label={entry.label}
                required={entry.required}
                colSpan={entry.colSpan}
              >
                <TextField
                  numeric={entry.numeric}
                  disabled={lockForm}
                />
              </FormField>
            ))}
          </FormSection>
        ))
      ) : (
        <FormSection
          title={t('emissions.directEmbedded', 'Direct Embedded Emissions')}
          compact
        >
          <FormField
            name="specificDirectEmission"
            anchor={recordFieldAnchor('specificDirectEmission')}
            label={t(
              'emissions.specificDirect',
              'Specific (direct) embedded emissions (tCO2/tonne of product)',
            )}
            required
            colSpan={12}
          >
            <TextField
              numeric
              disabled={lockForm}
            />
          </FormField>
        </FormSection>
      )}

      {/* Indirect Embedded Emissions, EU only: the electricity breakdown and share of default
          (D13), from which the indirect figure is calculated. */}
      {!isUk && (
        <FormSection
          title={t('emissions.indirectEmbedded', 'Indirect Embedded Emissions')}
          compact
        >
          <FormField
            name="indirectEmissionFactor"
            label={t('emissions.electricityEf', 'Electricity emission factor (tCO2/MWh)')}
            colSpan={6}
          >
            <TextField
              numeric
              disabled={lockForm}
            />
          </FormField>

          <FormField
            name="indirectEmissionFactorSourceValue"
            label={t('emissions.electricityEfSource', 'Electricity emission factor data source')}
            colSpan={6}
          >
            <TextField disabled={lockForm} />
          </FormField>

          <FormField
            name="electricityConsumed"
            label={t(
              'emissions.electricityConsumed',
              'Electricity consumed (MWh/tonne of product)',
            )}
            colSpan={6}
          >
            <TextField
              numeric
              disabled={lockForm}
            />
          </FormField>

          <FormField
            name="electricitySource"
            label={t('emissions.electricitySource', 'Electricity source')}
            required
            colSpan={6}
          >
            <SelectField
              options={electricitySourceOptions}
              disabled={lockForm}
            />
          </FormField>

          <FormField
            name="specificIndirectEmission"
            label={t(
              'emissions.specificIndirect',
              'Specific (indirect) embedded emissions (tCO2/tonne of product)',
            )}
            colSpan={6}
          >
            <TextField disabled={true} />
          </FormField>

          <FormField
            name="otherSourceIndication"
            label={t(
              'emissions.otherSourceIndication',
              'Other Source Indication for (Indirect) Emission Factor',
            )}
            colSpan={6}
          >
            <TextField disabled={lockForm} />
          </FormField>

          <FormField
            name="shareDefaultEmissions"
            label={t('emissions.shareDefault', 'Share of Default Emissions (%)')}
            colSpan={6}
          >
            <TextField
              numeric
              disabled={lockForm}
            />
          </FormField>
        </FormSection>
      )}

      {/* SEFA/Benchmark Section — operator and declarant contexts */}
      {!isUk && (contextType === 'operator' || contextType === 'declarant') && (
        <div className="mt-2">
          <p className={sectionTitleClass}>{t('benchmark.andSefaHeading', 'Benchmark & SEFA')}</p>
          <div className="mb-4 p-4 bg-slate-50 rounded border border-slate-200 relative">
            {isFetchingBenchmark && <CgBlockLoader />}

            {/* Pre-populated from CustomBenchmark notification (declarant only) */}
            {contextType === 'declarant' &&
              benchmarkPrePopulated &&
              (() => {
                const isOutdated = prePopulatedYear && prePopulatedYear < reportingYear;
                return (
                  <div
                    role="alert"
                    className={cn(
                      'relative mb-4 rounded-md border',
                      isOutdated
                        ? 'bg-warningSubtle text-warningEmphasis border-warningBorder'
                        : 'bg-infoSubtle text-infoEmphasis border-infoBorder',
                    )}
                    style={{ fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                  >
                    <button
                      type="button"
                      onClick={() => setBenchmarkPrePopulated(false)}
                      aria-label={t('common:actions.close', 'Close')}
                      className="absolute right-2 top-1.5 text-current hover:opacity-70"
                    >
                      <FontAwesomeIcon icon={faIcons.faXmark} />
                    </button>
                    <FontAwesomeIcon
                      icon={isOutdated ? faIcons.faExclamationTriangle : faIcons.faInfoCircle}
                      className="me-1"
                    />
                    {isOutdated ? (
                      <>
                        {t('fields.prePopulatedFrom', 'Pre-populated from a')}{' '}
                        <strong>{prePopulatedYear}</strong>{' '}
                        {t(
                          'benchmark.prePopulatedOutdatedPost',
                          'Custom Benchmark. No {{reportingYear}} benchmark exists yet — edit the value below to save an updated one, or switch to "Default EU Benchmark".',
                          { reportingYear },
                        )}
                      </>
                    ) : (
                      t(
                        'benchmark.prePopulatedCurrent',
                        'Pre-populated from existing Custom Benchmark — edit the value below to update it.',
                      )
                    )}
                  </div>
                );
              })()}

            {contextType === 'declarant' ? (
              <div
                className="mb-3"
                {...gapGroupProps}
              >
                <label
                  className="block mb-2"
                  style={{ fontSize: '0.875rem' }}
                  {...gapPartProps}
                >
                  {t('benchmark.source', 'Benchmark Source')}
                </label>
                <div
                  className="flex flex-wrap gap-3"
                  role="radiogroup"
                  aria-label={t('benchmark.sourceAlt', 'Benchmark source')}
                  {...controlAnchorProps(recordFieldAnchor('useBenchmarkDefault'))}
                >
                  {[
                    {
                      value: 'default',
                      title: t('benchmark.defaultTitle', 'Default Benchmark'),
                      description: t(
                        'benchmark.defaultDesc',
                        'Default benchmark values published by the European Commission.',
                      ),
                    },
                    {
                      value: 'custom',
                      title: t('benchmark.customTitle', 'Custom Benchmark'),
                      description: t(
                        'benchmark.customDesc',
                        'Reuse or save a benchmark for this supplier + installation.',
                      ),
                    },
                  ].map((opt) => {
                    const isDefault = opt.value === 'default';
                    const selected = isDefault
                      ? useBenchmarkDefault === true
                      : !useBenchmarkDefault;
                    const handleSelect = () => {
                      if (lockForm) return;
                      if (isDefault) {
                        handleUseBenchmarkDefaultChange(true);
                        setBenchmarkPrePopulated(false);
                        setNoCustomBenchmarkFound(false);
                      } else {
                        handleUseBenchmarkDefaultChange(false);
                        if (cnGood && reportingYear) fetchExistingCustomBenchmark();
                      }
                    };
                    const cardCls = `flex-1 min-w-[240px] rounded-lg border-2 p-3 transition ${
                      selected
                        ? 'border-primaryColor bg-primaryColor/5'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    } ${lockForm ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`;
                    return (
                      <label
                        key={opt.value}
                        className={cardCls}
                      >
                        <input
                          type="radio"
                          name="benchmarkSource"
                          className="sr-only"
                          checked={selected}
                          onChange={handleSelect}
                          disabled={lockForm}
                        />
                        <div className="flex items-start gap-2">
                          <span
                            className={`mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                              selected ? 'border-primaryColor' : 'border-slate-300'
                            }`}
                          >
                            {selected && <span className="h-2 w-2 rounded-full bg-primaryColor" />}
                          </span>
                          <div className="grow">
                            <div
                              className="flex items-center gap-2"
                              style={{ fontSize: '0.875rem', fontWeight: 500 }}
                            >
                              {opt.title}
                              {!isDefault && isFetchingCustomBenchmark && (
                                <FontAwesomeIcon
                                  icon={faIcons.faSpinner}
                                  spin
                                  style={{ fontSize: '0.75rem' }}
                                />
                              )}
                            </div>
                            <div
                              className="text-slate-500 mt-0.5"
                              style={{ fontSize: '0.75rem' }}
                            >
                              {opt.description}
                            </div>
                            {isDefault &&
                              useBenchmarkDefault &&
                              benchmark &&
                              !isFetchingBenchmark && (
                                <div
                                  className="text-successColor mt-1"
                                  style={{ fontSize: '0.75rem' }}
                                >
                                  <FontAwesomeIcon
                                    icon={faIcons.faCheck}
                                    className="me-1"
                                  />
                                  {t('benchmark.loaded', 'Benchmark loaded')}
                                </div>
                              )}
                            {!isDefault && noCustomBenchmarkFound && !useBenchmarkDefault && (
                              <div
                                className="text-slate-500 mt-1"
                                style={{ fontSize: '0.75rem' }}
                              >
                                <FontAwesomeIcon
                                  icon={faIcons.faInfoCircle}
                                  className="me-1"
                                />
                                {t(
                                  'benchmark.noSavedBenchmark',
                                  'No saved benchmark — enter one below to save it.',
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div
                className="flex items-center gap-5 mb-4"
                {...gapGroupProps}
              >
                <input
                  type="checkbox"
                  id="useBenchmarkDefault"
                  {...controlAnchorProps(recordFieldAnchor('useBenchmarkDefault'))}
                  checked={useBenchmarkDefault ?? true}
                  onChange={(e) => handleUseBenchmarkDefaultChange(e.target.checked)}
                  disabled={lockForm}
                  className="me-2"
                />
                <label
                  htmlFor="useBenchmarkDefault"
                  style={{ fontSize: '0.875rem', marginBottom: 0, cursor: 'pointer' }}
                  {...gapPartProps}
                >
                  {t('benchmark.useDefault', 'Use Default EU Benchmark')}
                </label>
                {useBenchmarkDefault && benchmark && !isFetchingBenchmark && (
                  <span
                    className="ms-2 text-successColor"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <FontAwesomeIcon
                      icon={faIcons.faCheck}
                      className="me-1"
                    />
                    {t('benchmark.loaded', 'Benchmark loaded')}
                  </span>
                )}
              </div>
            )}

            {!useBenchmarkDefault &&
              (() => {
                const isBenchmarkMode = (benchmarkInputMode || 'Benchmark') === 'Benchmark';
                const segmentBase =
                  'rounded px-3 py-1 text-xs transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300';
                const segmentActive = 'bg-primaryColor text-white font-medium shadow-sm';
                const segmentInactive = 'text-slate-500 hover:text-primaryColor';
                return (
                  <div className="mb-3">
                    <div
                      role="tablist"
                      onKeyDown={handleTablistKeys}
                      aria-label={t('sefa.inputMode', 'Input mode')}
                      className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5"
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={isBenchmarkMode}
                        onClick={() => handleBenchmarkInputModeChange('Benchmark')}
                        className={`${segmentBase} ${isBenchmarkMode ? segmentActive : segmentInactive}`}
                      >
                        {t('benchmark.enter', 'Enter Benchmark')}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={!isBenchmarkMode}
                        onClick={() => handleBenchmarkInputModeChange('SEFA')}
                        className={`${segmentBase} ${!isBenchmarkMode ? segmentActive : segmentInactive}`}
                      >
                        {t('sefa.enter', 'Enter SEFA')}
                      </button>
                    </div>
                  </div>
                );
              })()}

            <div className="grid grid-cols-12 gap-4">
              {(useBenchmarkDefault || benchmarkInputMode === 'Benchmark') && (
                <div
                  className="col-span-6"
                  {...gapGroupProps}
                >
                  <div
                    className="flex items-center gap-1.5 mb-1"
                    {...gapPartProps}
                  >
                    <label style={{ fontSize: '0.875rem', marginBottom: 0 }}>
                      {t('benchmark.unit', 'Benchmark (tCO2e/tonne)')}
                    </label>
                    <CgInfoHint
                      title={t('benchmark.cbamBenchmarkPopoverTitle', 'CBAM Benchmark')}
                      position="top"
                      maxWidth="max-w-[448px]"
                    >
                      <div className="space-y-3">
                        <div>
                          <p className="mb-1 font-semibold text-gray-700">
                            {t('benchmark.whatIs', 'What is the CBAM Benchmark?')}
                          </p>
                          <p className="mb-0">
                            {t(
                              'benchmark.whatIsBody',
                              'The CBAM benchmark is the emissions intensity (tCO₂e/tonne of product) of the 10% most efficient EU installations producing that good under the EU ETS.',
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="mb-1 font-semibold text-gray-700">
                            {t('benchmark.howBrandHandles', 'How {{brand}} handles this:', {
                              brand: brand.name,
                            })}
                          </p>
                          <ul className="mb-0 list-disc pl-5 space-y-1">
                            <li>
                              {t(
                                'sefa.enterHint',
                                'If you already know your SEFA, you can enter it directly.',
                              )}
                            </li>
                            <li>
                              {t(
                                'sefa.benchmarkHint',
                                'If you enter a Benchmark, we calculate your SEFA automatically.',
                              )}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </CgInfoHint>
                  </div>
                  <input
                    type="text"
                    value={benchmark ?? ''}
                    onChange={(e) => handleBenchmarkChange(e.target.value)}
                    disabled={lockForm || useBenchmarkDefault}
                    className={inputCls(false)}
                    {...controlAnchorProps(recordFieldAnchor('benchmark'))}
                  />
                  {useBenchmarkDefault && !cnGood && (
                    <small
                      className="text-secondaryColor"
                      style={{ fontSize: '0.75rem' }}
                    >
                      <FontAwesomeIcon
                        icon={faIcons.faInfoCircle}
                        className="me-1"
                      />
                      {t(
                        'benchmark.selectCnGoodAutofetch',
                        'Select a CN Good to auto-fetch benchmark',
                      )}
                    </small>
                  )}
                  {useBenchmarkDefault && cnGood && !dateValue && (
                    <small
                      className="text-secondaryColor"
                      style={{ fontSize: '0.75rem' }}
                    >
                      <FontAwesomeIcon
                        icon={faIcons.faInfoCircle}
                        className="me-1"
                      />
                      {t(
                        'benchmark.selectPeriodAutofetch',
                        'Select a reporting period to auto-fetch benchmark',
                      )}
                    </small>
                  )}
                </div>
              )}

              <div
                className={
                  useBenchmarkDefault || benchmarkInputMode === 'Benchmark'
                    ? 'col-span-6'
                    : 'col-span-12'
                }
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <label style={{ fontSize: '0.875rem', marginBottom: 0 }}>
                    {t('sefa.unit', 'SEFA (tCO2e/tonne)')}
                  </label>
                  <CgInfoHint
                    title={t('sefa.popoverTitle', 'SFA & SEFA')}
                    position="top"
                    maxWidth="max-w-[448px]"
                  >
                    <div className="space-y-3">
                      <div>
                        <p className="mb-1 font-semibold text-gray-700">
                          {t('sefa.full', 'SEFA (Specific Embedded Free Allocation)')}
                        </p>
                        <p className="mb-0">
                          {t(
                            'sefa.fullBody',
                            'Represents the portion of emissions per tonne of product that would have been granted for free under the EU ETS rules.',
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="mb-1 font-semibold text-gray-700">
                          {t('benchmark.howBrandHandles', 'How {{brand}} handles this:', {
                            brand: brand.name,
                          })}
                        </p>
                        <ul className="mb-0 list-disc pl-5 space-y-1">
                          <li>
                            {t(
                              'sefa.enterHint',
                              'If you already know your SEFA, you can enter it directly.',
                            )}
                          </li>
                          <li>
                            {t(
                              'sefa.benchmarkHint',
                              'If you enter a Benchmark, we calculate your SEFA automatically.',
                            )}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CgInfoHint>
                </div>
                <input
                  type="text"
                  value={sefa ?? ''}
                  onChange={(e) => handleSefaChange(e.target.value)}
                  disabled={lockForm || useBenchmarkDefault || benchmarkInputMode === 'Benchmark'}
                  className={inputCls(false)}
                />
                {(useBenchmarkDefault || benchmarkInputMode === 'Benchmark') && benchmark && (
                  <small
                    className="text-secondaryColor"
                    style={{ fontSize: '0.75rem' }}
                  >
                    <FontAwesomeIcon
                      icon={faIcons.faInfoCircle}
                      className="me-1"
                    />
                    {t(
                      'sefa.autoCalculatedHint',
                      'Auto-calculated: Benchmark × (CBAM Factor {{factor}}%)',
                      {
                        factor: cbamFactor,
                      },
                    )}
                  </small>
                )}
              </div>
            </div>

            {reportingYear && cbamFactor && (
              <div className="mt-2">
                <small
                  className="text-secondaryColor"
                  style={{ fontSize: '0.75rem' }}
                >
                  <FontAwesomeIcon
                    icon={faIcons.faCalendarAlt}
                    className="me-1"
                  />
                  {t(
                    'emissions.reportingYearFactorSummary',
                    'Reporting Year: {{year}} | CBAM Factor: {{factor}}%',
                    { year: reportingYear, factor: cbamFactor },
                  )}
                </small>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
